import axios from "axios";

const API = "/api";

const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Config
export const getConfig = async () => {
  const response = await api.get("/config");
  return response.data;
};

// Spots
export const getSpots = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.category) params.append("category", filters.category);
  if (filters.tags) params.append("tags", filters.tags);
  if (filters.search) params.append("search", filters.search);
  if (filters.status) params.append("status", filters.status);
  if (filters.includeDrafts) params.append("include_drafts", "true");
  if (filters.includeAll) params.append("include_all", "true");
  if (filters.creatorId) params.append("creator_id", filters.creatorId);
  if (filters.hasReports !== undefined) params.append("has_reports", filters.hasReports ? "true" : "false");
  params.append("apply_fuzz", filters.applyFuzz !== false ? "true" : "false");
  
  const response = await api.get(`/spots?${params.toString()}`);
  return response.data;
};

export const getSpot = async (spotId) => {
  const response = await api.get(`/spots/${spotId}?apply_fuzz=false`);
  return response.data;
};

export const createSpot = async (spotData) => {
  const response = await api.post("/spots", spotData);
  return response.data;
};

export const getNearbySpots = async (lat, lng, radius = 250) => {
  const response = await api.get(`/spots/nearby?lat=${lat}&lng=${lng}&radius=${radius}`);
  return response.data;
};

export const checkSpotLocation = async (lat, lng) => {
  const response = await api.get(`/spots/location-check?lat=${lat}&lng=${lng}`);
  return response.data;
};

export const getActiveMessage = async () => {
  const response = await api.get("/messages/active");
  return response.data;
};

export const acknowledgeMessage = async (messageId) => {
  const response = await api.post(`/messages/${messageId}/acknowledge`);
  return response.data;
};

export const publishAdminMessage = async (content, isImportant = false, expiresAt = null) => {
  const response = await api.post("/admin/messages", { content, is_important: isImportant, expires_at: expiresAt });
  return response.data;
};

export const getAdminMessages = async () => {
  const response = await api.get("/admin/messages");
  return response.data;
};

export const updateAdminMessage = async (id, payload) => {
  const response = await api.patch(`/admin/messages/${id}`, payload);
  return response.data;
};

export const deleteAdminMessage = async (id) => {
  await api.delete(`/admin/messages/${id}`);
};

export const sendBroadcastEmail = async (subject, body) => {
  const response = await api.post("/admin/broadcast", { subject, body });
  return response.data;
};

export const updateSpot = async (spotId, spotData) => {
  const response = await api.put(`/spots/${spotId}`, spotData);
  return response.data;
};

export const deleteSpot = async (spotId) => {
  const response = await api.delete(`/spots/${spotId}`);
  return response.data;
};

export const publishSpot = async (spotId) => {
  const response = await api.post(`/spots/${spotId}/publish`);
  return response.data;
};

export const maskSpot = async (spotId) => {
  const response = await api.post(`/spots/${spotId}/mask`);
  return response.data;
};

export const unmaskSpot = async (spotId) => {
  const response = await api.post(`/spots/${spotId}/unmask`);
  return response.data;
};

// Photos
export const uploadPhoto = async (spotId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(`/spots/${spotId}/photos`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const deletePhoto = async (spotId, filename) => {
  const response = await api.delete(`/spots/${spotId}/photos/${filename}`);
  return response.data;
};

export const checkPhoto = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/photos/check", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const forcePhoto = async (filename) => {
  const response = await api.post(`/photos/${filename}/force`);
  return response.data;
};

// Reports
export const createReport = async (reportData) => {
  const response = await api.post("/reports", reportData);
  return response.data;
};

export const getReports = async (status = null) => {
  const params = status ? `?status=${status}` : "";
  const response = await api.get(`/reports${params}`);
  return response.data;
};

export const updateReportStatus = async (reportId, status) => {
  const response = await api.put(`/reports/${reportId}/status?status=${status}`);
  return response.data;
};

// Traces
export const getSpotTraces = async (spotId) => {
  const response = await api.get(`/traces/spot/${spotId}`);
  return response.data;
};

export const getMyTrace = async (spotId) => {
  const response = await api.get(`/traces/my/${spotId}`);
  return response.data;
};

export const createOrUpdateTrace = async (spotId, traceType) => {
  const response = await api.post("/traces", { spot_id: spotId, trace_type: traceType });
  return response.data;
};

export const deleteTrace = async (spotId) => {
  const response = await api.delete(`/traces/${spotId}`);
  return response.data;
};

// Users (Me)
export const getMe = async () => {
  const response = await api.get("/users/me");
  return response.data;
};

export const getMyStats = async () => {
  const response = await api.get("/users/me/stats");
  return response.data;
};

export const getMySpots = async () => {
  const response = await api.get("/users/me/spots");
  return response.data;
};

export const getMyTraces = async () => {
  const response = await api.get("/users/me/traces");
  return response.data;
};

export const updateMyEmail = async (newEmail, password) => {
  const response = await api.put("/users/me/email", { new_email: newEmail, password });
  return response.data;
};

export const updateMyPassword = async (currentPassword, newPassword) => {
  const response = await api.put("/users/me/password", { current_password: currentPassword, new_password: newPassword });
  return response.data;
};

export const updateMyNotifications = async (payload) => {
  const response = await api.put("/users/me/notifications", payload);
  return response.data;
};

export const deleteMyAccount = async (password) => {
  const response = await api.post("/users/me/delete", { password });
  return response.data;
};

export const markOnboardingDone = async () => {
  const response = await api.post("/users/me/onboarding-done");
  return response.data;
};


// Users (Admin)
export const getUsers = async () => {
  const response = await api.get("/users");
  return response.data;
};

export const updateUserStatus = async (userId, status) => {
  const response = await api.put(`/users/${userId}/status?status=${status}`);
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await api.put(`/users/${userId}/role?role=${role}`);
  return response.data;
};

export const deleteUserAdmin = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

// Stats (Admin)
export const getStats = async () => {
  const response = await api.get("/stats");
  return response.data;
};

// Seed
export const seedDatabase = async () => {
  const response = await api.post("/seed");
  return response.data;
};

// Get full URL for photos
export const getPhotoUrl = (photoPath) => {
  if (!photoPath) return "";

  // Si le backend renvoie déjà une route /api/..., ne pas préfixer une 2e fois
  if (photoPath.startsWith("/api/")) return photoPath;

  // Sinon on passe par le proxy Nginx
  return `/api${photoPath.startsWith("/") ? "" : "/"}${photoPath}`;
};


export const promoteUserModerator = async (userId) => {
  const response = await api.put(`/users/${userId}/promote-moderator`);
  return response.data;
};

export const getModerationMyStats = async () => {
  const response = await api.get("/moderation/my-stats");
  return response.data;
};

export const getModerationPendingCount = async () => {
  const response = await api.get("/moderation/pending-count");
  return response.data;
};

export const getModerationReports = async () => {
  const response = await api.get("/moderation/reports");
  return response.data;
};

export const resolveUncheckedSpot = async (spotId) => {
  const response = await api.put(`/moderation/spots/${spotId}/resolve`);
  return response.data;
};

export default api;
